import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

import { API } from "../config/api";

const TechnicalIndicators = ({ ticker }) => {
  const [indicators, setIndicators] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticker) {
      fetchIndicators();
    }
  }, [ticker]);

  const fetchIndicators = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/technical-indicators/${ticker}`);
      setIndicators(response.data);
    } catch (error) {
      console.error("Error fetching technical indicators:", error);
      console.warn("Failed to load technical indicators - retrying silently in background");
    } finally {
      setLoading(false);
    }
  };

  const getSignalColor = (signal) => {
    if (signal === 'Overbought') return 'bg-red-100 text-red-700';
    if (signal === 'Oversold') return 'bg-green-100 text-green-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getSignalIcon = (signal) => {
    if (signal === 'Overbought') return TrendingDown;
    if (signal === 'Oversold') return TrendingUp;
    return Minus;
  };

  const getValueColor = (value, type) => {
    if (type === 'rsi') {
      if (value > 70) return 'text-red-600';
      if (value < 30) return 'text-green-600';
    } else if (type === 'mfi') {
      if (value > 80) return 'text-red-600';
      if (value < 20) return 'text-green-600';
    }
    return 'text-slate-900';
  };

  return (
    <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Technical Indicators - RSI & MFI
        </CardTitle>
        <p className="text-xs text-slate-600">
          Momentum oscillators to identify overbought and oversold conditions
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-slate-600">Calculating indicators...</span>
          </div>
        ) : indicators ? (
          <div className="space-y-6">
            {/* Current Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* RSI Card */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-900">Relative Strength Index (RSI)</h4>
                  <Badge className={getSignalColor(indicators.rsi.signal)}>
                    {indicators.rsi.signal}
                  </Badge>
                </div>
                <div className={`text-4xl font-bold mb-2 ${getValueColor(indicators.rsi.value, 'rsi')}`}>
                  {indicators.rsi.value !== null ? indicators.rsi.value.toFixed(2) : 'N/A'}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600 mb-3">
                  <div className="flex-1">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${indicators.rsi.value > 70 ? 'bg-red-500' : indicators.rsi.value < 30 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${indicators.rsi.value}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px]">
                      <span>0 (Oversold)</span>
                      <span>50</span>
                      <span>100 (Overbought)</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-600">{indicators.rsi.interpretation}</p>
              </div>

              {/* MFI Card */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-900">Money Flow Index (MFI)</h4>
                  <Badge className={getSignalColor(indicators.mfi.signal)}>
                    {indicators.mfi.signal}
                  </Badge>
                </div>
                <div className={`text-4xl font-bold mb-2 ${getValueColor(indicators.mfi.value, 'mfi')}`}>
                  {indicators.mfi.value !== null ? indicators.mfi.value.toFixed(2) : 'N/A'}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600 mb-3">
                  <div className="flex-1">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${indicators.mfi.value > 80 ? 'bg-red-500' : indicators.mfi.value < 20 ? 'bg-green-500' : 'bg-purple-500'}`}
                        style={{ width: `${indicators.mfi.value}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px]">
                      <span>0 (Oversold)</span>
                      <span>50</span>
                      <span>100 (Overbought)</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-600">{indicators.mfi.interpretation}</p>
              </div>
            </div>

            {/* 30-Day Chart */}
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">30-Day Trend</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={indicators.chart_data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    yAxisId="left"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    label={{ value: 'RSI / MFI', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  
                  {/* Reference lines for overbought/oversold */}
                  <ReferenceLine yAxisId="left" y={70} stroke="#ef4444" strokeDasharray="3 3" label={{ value: 'RSI Overbought (70)', fontSize: 10 }} />
                  <ReferenceLine yAxisId="left" y={30} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'RSI Oversold (30)', fontSize: 10 }} />
                  <ReferenceLine yAxisId="left" y={80} stroke="#dc2626" strokeDasharray="5 5" label={{ value: 'MFI Overbought (80)', fontSize: 10 }} />
                  <ReferenceLine yAxisId="left" y={20} stroke="#059669" strokeDasharray="5 5" label={{ value: 'MFI Oversold (20)', fontSize: 10 }} />
                  
                  <Line yAxisId="left" type="monotone" dataKey="rsi" stroke="#ef4444" strokeWidth={2.5} name="RSI" dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="mfi" stroke="#22c55e" strokeWidth={2.5} name="MFI" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Interpretation Guide */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h5 className="text-sm font-semibold text-slate-900 mb-2">How to Use These Indicators</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-700">
                <div>
                  <p className="font-semibold mb-1">RSI (Relative Strength Index)</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Above 70: Potentially overbought, consider selling</li>
                    <li>Below 30: Potentially oversold, consider buying</li>
                    <li>Measures price momentum over 14 periods</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-1">MFI (Money Flow Index)</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Above 80: Overbought with high volume</li>
                    <li>Below 20: Oversold with low volume</li>
                    <li>Combines price and volume data</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-600 py-8">No indicator data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TechnicalIndicators;
